import { DownloadOutlined } from '@ant-design/icons';
import AppAlert from '@nxsys/components/AppAlert';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppPayFrequencySelect from '@nxsys/components/AppPayFrequencySelect';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import {
  useExportXeroJournal,
  useGetXeroSettings,
  usePreviewXeroJournal,
} from '@nxsys/hooks/api/accounting';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import {
  JournalLineKey,
  JournalMode,
  NominalMapping,
  XeroJournalPreview,
  XeroJournalRequest,
} from '@nxsys/types/api/accounting';
import { Option, PaymentFrequencyType } from '@nxsys/types/models/Common';
import { downloadFileByUrlWithCorrectFileName } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getRangeYear } from '@nxsys/utils/taxYear';
import { orderBy } from 'lodash';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  blankMappingKeys,
  formatGbp,
  MODE_OPTIONS,
  OUTPUT_OPTIONS,
  XeroJournalPreviewLine,
} from './constants';
import XeroNominalMappingModal from './XeroNominalMappingModal';

/**
 * NX-5235 — Accounting › Xero. Filter bar on top (placeholder-only controls,
 * house style per P30P32Report), journal preview table below, download
 * top-right. Apply seeds the nominal-code window from the company's saved
 * settings; Continue builds the preview; edits are one-off per export —
 * persistence lives only in Company Settings › Integration › Xero Settings.
 */
const XeroJournalPage = () => {
  const { messages } = useTypedIntl();
  const { isBureau, user, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const latestTaxYear = orderBy(
    getRangeYear(payrollCountry === IRELAND),
    'value',
    'desc',
  )[0]?.value as number | undefined;

  // Filters (owned locally; no form — placeholder-only inline controls)
  const [company, setCompany] = useState<Option<string> | null>(null);
  const [payFrequency, setPayFrequency] = useState<PaymentFrequencyType>();
  const [taxYear, setTaxYear] = useState<number | undefined>(latestTaxYear);
  const [taxPeriod, setTaxPeriod] = useState<number | undefined>(undefined);
  const [mode, setMode] = useState<JournalMode>('INDIVIDUAL');

  // Mapping window — seeded from GET /xero-settings per company
  const [isMappingOpen, setIsMappingOpen] = useState(false);
  const [mapping, setMapping] = useState<NominalMapping>({});
  const [settingsCompanyId, setSettingsCompanyId] = useState<string | null>(
    null,
  );
  const [requiredKeys, setRequiredKeys] = useState<JournalLineKey[]>([]);
  const [missingKeys, setMissingKeys] = useState<JournalLineKey[]>([]);
  const [busy, setBusy] = useState(false);

  const [preview, setPreview] = useState<XeroJournalPreview | null>(null);
  const lines = preview?.lines ?? [];
  const totals = preview?.totals ?? null;

  const { run: fetchSettings } = useGetXeroSettings();
  const { run: runPreview } = usePreviewXeroJournal();
  const { run: runExport, loading: exporting } = useExportXeroJournal();

  const buildRequest = (): XeroJournalRequest | null => {
    if (!company?.value || !payFrequency || !taxYear || !taxPeriod) return null;
    return {
      companyId: company.value,
      payFrequency,
      taxYear,
      taxPeriod,
      mode,
      mapping,
    };
  };

  /** Apply: seed the code window from the company's saved settings, open it. */
  const handleApply = async () => {
    if (!company?.value || !payFrequency || !taxYear || !taxPeriod) {
      showMessage({
        type: 'error',
        content: messages['accounting.xeroJournal.fillFilters'],
      });
      return;
    }
    setBusy(true);
    try {
      if (settingsCompanyId !== company.value) {
        const settings = await fetchSettings(company.value);
        setMapping(settings.mapping);
        setSettingsCompanyId(company.value);
      }
      setMissingKeys([]);
      setIsMappingOpen(true);
    } catch {
      // toast raised by the hook
    } finally {
      setBusy(false);
    }
  };

  /** Continue: build the preview; blank codes on valued lines keep it open. */
  const handleMappingContinue = async () => {
    const request = buildRequest();
    if (!request) return;
    setBusy(true);
    try {
      const p = await runPreview(request);
      if (p.missingCodes.length) {
        setMissingKeys(p.missingCodes);
        setRequiredKeys(p.missingCodes);
        showMessage({
          type: 'error',
          content: messages['accounting.xeroJournal.missingCodes'],
        });
        return;
      }
      setMissingKeys([]);
      setPreview(p);
      setIsMappingOpen(false);
    } catch {
      // toast raised by the hook
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    const request = buildRequest();
    if (!request || !preview?.lines.length) return;
    try {
      const res = await runExport(request);
      await downloadFileByUrlWithCorrectFileName(res.data, res.fileName);
      showMessage({
        type: 'success',
        content: messages['accounting.xeroJournal.downloaded'],
      });
    } catch {
      // toast raised by the hook
    }
  };

  const columns: ColumnsType<XeroJournalPreviewLine> = useMemo(
    () => [
      {
        title: messages['common.date'],
        dataIndex: 'date',
        width: 150,
        render: (v: string) => <span className="whitespace-nowrap">{v}</span>,
      },
      {
        title: messages['accounting.xeroJournal.reference'],
        dataIndex: 'reference',
      },
      {
        title: messages['accounting.xeroJournal.nominalCode'],
        render: (_, l) =>
          l.accountName ? `${l.accountCode} - ${l.accountName}` : l.accountCode,
      },
      {
        title: messages['accounting.xeroJournal.debit'],
        align: 'right',
        width: 160,
        render: (_, l) => formatGbp(l.debit),
      },
      {
        title: messages['accounting.xeroJournal.credit'],
        align: 'right',
        width: 160,
        render: (_, l) => formatGbp(l.credit),
      },
    ],
    [messages],
  );

  const renderTitle = () => (
    <AppPageTitle
      prefix={messages['sidebar.app.accounting']}
      separator=" / "
      title={messages['sidebar.app.accounting.xero']}
    />
  );

  const renderRightContent = () => (
    <div className="flex items-center gap-x-[8px]">
      <AppButton
        type="primary"
        name={messages['accounting.xeroJournal.download']}
        leftIcon={<DownloadOutlined />}
        onClick={handleDownload}
        loading={exporting}
        disabled={!lines.length || busy}
      />
    </div>
  );

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={false}
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4 items-center">
              <AppSelect
                className="!w-[220px] !min-w-0"
                options={OUTPUT_OPTIONS}
                defaultValue={OUTPUT_OPTIONS[0]}
              />
              <AppSelectSync
                className="!w-[220px]"
                type="company"
                apiType={isBureau ? 'searchBureauEmployer' : 'searchCompany'}
                paramFilter={{ companyIds: user?.companyUserIds || undefined }}
                placeholder={
                  isBureau
                    ? messages['common.employer']
                    : messages['common.paymentCompany']
                }
                value={company}
                onChangeValue={(value) =>
                  setCompany(value as Option<string> | null)
                }
              />
              <AppPayFrequencySelect
                placeholder={messages['common.payFrequency']}
                value={payFrequency}
                onChange={(value) => {
                  setPayFrequency(value ?? undefined);
                  setTaxPeriod(undefined);
                }}
              />
              <TaxYearSelect
                placeholder={messages['common.taxYear']}
                onFilter={({ taxYear: value }) => setTaxYear(value)}
              />
              <AppTaxWeekMonthSelect
                key={payFrequency ?? 'none'}
                placeholder={messages['common.taxPeriod']}
                value={taxPeriod}
                paymentFrequency={payFrequency}
                disabled={!payFrequency}
                onChange={(value?: number) => setTaxPeriod(value)}
              />
              <AppSelect
                className="!w-[230px] !min-w-0"
                options={MODE_OPTIONS}
                defaultValue={MODE_OPTIONS[0]}
                allowCapitalize={false}
                onChange={(option: { value: JournalMode }) =>
                  setMode(option?.value ?? 'INDIVIDUAL')
                }
              />
              <AppButton
                type="primary"
                name={messages['common.apply']}
                onClick={handleApply}
                loading={busy && !isMappingOpen}
                disabled={busy}
              />
            </div>
            {totals && (
              <div className="flex items-center">
                <Typography.Text strong>
                  {messages['accounting.xeroJournal.totalRecords']}:{' '}
                  {lines.length} ·{' '}
                  {messages['accounting.xeroJournal.totalDebit']}:{' '}
                  {formatGbp(totals.debit)} ·{' '}
                  {messages['accounting.xeroJournal.totalCredit']}:{' '}
                  {formatGbp(totals.credit)}
                </Typography.Text>
              </div>
            )}
          </div>
        </AppsHeader>
        <AppsContent>
          {preview && preview.lines.length > 0 && (
            <AppAlert
              className="mb-3"
              variant="info"
              message={preview.disclaimer}
            />
          )}
          <AppTableContainer<XeroJournalPreviewLine>
            data={lines}
            rowKey={(_, i) => String(i)}
            columns={columns}
            loading={false}
            page={1}
            pageSize={lines.length || 10}
            total={lines.length}
            pagination={false}
            noDataType="noDataToShow"
            titleEmpty={messages['accounting.xeroJournal.applyToPreview']}
            scroll={{ x: '100%', y: 700 }}
          />
        </AppsContent>
      </AppCard>

      <XeroNominalMappingModal
        isOpen={isMappingOpen}
        toggle={() => setIsMappingOpen(false)}
        mapping={mapping}
        onChange={(m) => {
          setMapping(m);
          if (missingKeys.length)
            setMissingKeys(blankMappingKeys(m, requiredKeys));
        }}
        requiredKeys={requiredKeys}
        missingKeys={missingKeys}
        onContinue={handleMappingContinue}
        loading={busy}
      />
    </AppsContainer>
  );
};

export default XeroJournalPage;
